import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Wrench, CheckCircle2, Loader2, ShieldCheck, AlertTriangle, CalendarClock, Droplets, UserRound, ArrowUpRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { OilTrac } from "@/components/OilTrac";
import { FieldError } from "@/components/BookingSection";
import { PHONES, isValidEmail, isValidPhone } from "@/lib/site";

const inputCls =
  "w-full border border-white/10 bg-[#0D1017] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-200 focus:border-amber-600/60";
const errorInputCls = "border-red-500/60 focus:border-red-500";

const validateLookup = ({ rego, contact }) => {
  const errors = {};
  if (rego.replace(/\s/g, "").length < 2) errors.rego = "Enter your rego plate (e.g. ABC123).";
  const c = contact.trim();
  if (!c) errors.contact = "Enter the phone number or email you gave the workshop.";
  else if (c.includes("@") ? !isValidEmail(c) : !isValidPhone(c)) errors.contact = c.includes("@") ? "That email address doesn't look right." : "That phone number doesn't look right.";
  return errors;
};

const fmtDate = (d) => {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return d;
  }
};

export const ServiceHistory = () => {
  const [rego, setRego] = useState("");
  const [contact, setContact] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const lookup = async (e) => {
    e.preventDefault();
    const found = validateLookup({ rego, contact });
    setFieldErrors(found);
    if (Object.keys(found).length) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await api.post("/service-history/lookup", { rego, contact });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("No records found for those details. Check the rego and the phone or email we have on file — or call us and we'll find it for you.");
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="history" className="border-y border-white/5 bg-[#0A0C10] py-24 lg:py-32" data-testid="service-history-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              className="font-mono text-xs uppercase tracking-[0.3em] text-amber-500"
            >
              Service Records Portal
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ delay: 0.1 }}
              className="mt-4 font-display text-4xl uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl"
            >
              Your car's life at Sagittarius. On record.
            </motion.h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-400">
              Every job we do on your vehicle is logged — date, odometer, parts, cost, and our Oil Trac
              system tells you exactly when your next oil change is due. Enter your rego and the phone
              or email you gave us to see it all, any time.
            </p>

            <div className="mt-10 border border-white/10 bg-[#12151C] p-6 sm:p-8">
              <div className="flex items-center gap-3">
                <Wrench size={20} className="text-amber-500" />
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-slate-300">Every record shows</span>
              </div>
              <ul className="mt-5 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
                {[
                  "Date & odometer reading",
                  "Work carried out, itemised",
                  "Parts used & cost",
                  "Next service due date",
                  "Oil Trac — next oil change km & date",
                  "Oil grade used on your engine",
                ].map((c) => (
                  <li key={c} className="flex items-start gap-2.5 text-sm text-slate-400">
                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-amber-500" /> {c}
                  </li>
                ))}
              </ul>
            </div>

            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">
              Private — only you can open your records
            </p>

            <Link
              to="/portal"
              data-testid="history-portal-cta"
              className="group mt-8 flex items-center justify-between border border-amber-600/40 bg-amber-600/5 px-6 py-5 transition-colors duration-200 hover:border-amber-600 hover:bg-amber-600/10"
            >
              <div>
                <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-amber-500"><UserRound size={14} /> Customer Portal</div>
                <p className="mt-2 text-sm text-slate-400">Create a free account to see all your vehicles, Oil Trac, photos and pay invoices online.</p>
              </div>
              <ArrowUpRight size={18} className="shrink-0 text-amber-500 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="border border-white/10 bg-[#12151C] p-6 sm:p-10" data-testid="history-lookup-card">
            <form onSubmit={lookup} noValidate className="space-y-5" data-testid="history-lookup-form">
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Rego plate</label>
                <input
                  value={rego}
                  onChange={(e) => { setRego(e.target.value); setFieldErrors((f) => ({ ...f, rego: undefined })); }}
                  placeholder="ABC123"
                  className={`${inputCls} font-mono uppercase tracking-[0.2em] ${fieldErrors.rego ? errorInputCls : ""}`}
                  aria-invalid={!!fieldErrors.rego}
                  data-testid="lookup-rego-input"
                />
                <FieldError id="lookup-rego" message={fieldErrors.rego} />
              </div>
              <div>
                <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Phone or email on file</label>
                <input
                  value={contact}
                  onChange={(e) => { setContact(e.target.value); setFieldErrors((f) => ({ ...f, contact: undefined })); }}
                  placeholder="021 123 4567 or you@example.com"
                  className={`${inputCls} ${fieldErrors.contact ? errorInputCls : ""}`}
                  aria-invalid={!!fieldErrors.contact}
                  data-testid="lookup-contact-input"
                />
                <FieldError id="lookup-contact" message={fieldErrors.contact} />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="lookup-submit-button"
                className="flex w-full items-center justify-center gap-3 bg-amber-600 py-4 font-mono text-sm uppercase tracking-[0.2em] text-black transition-colors duration-200 hover:bg-amber-500 disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                {loading ? "Searching…" : "View my service history"}
              </button>
            </form>

            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-6 border border-red-500/40 bg-red-500/10 px-4 py-3 text-xs leading-relaxed text-red-300"
                  data-testid="lookup-error"
                >
                  <div className="flex items-center gap-2 font-mono uppercase tracking-[0.15em]">
                    <AlertTriangle size={14} /> No match
                  </div>
                  <p className="mt-2">{error}</p>
                  <div className="mt-2 flex flex-wrap gap-4">
                    {PHONES.map((p) => (
                      <a key={p.id} href={p.tel} className="inline-block font-mono text-red-200 underline underline-offset-4" data-testid={`lookup-error-phone-${p.id}`}>{p.display}</a>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {result && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="mt-8"
                  data-testid="history-results"
                >
                  <div className="mb-5 flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-300" data-testid="lookup-success">
                    <CheckCircle2 size={14} /> Found {result.records.length} service record{result.records.length === 1 ? "" : "s"} for {result.rego}.
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 pb-4">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Records for</div>
                      <div className="mt-1 text-lg font-bold text-slate-100" data-testid="history-owner">{result.customer_name}</div>
                      {result.vehicle && <div className="text-sm text-slate-400">{result.vehicle}</div>}
                    </div>
                    <span className="border border-teal-500/40 bg-teal-500/10 px-3 py-1.5 font-mono text-sm tracking-[0.2em] text-teal-400" data-testid="history-rego-chip">
                      {result.rego}
                    </span>
                  </div>
                  {(result.contact_phone || result.contact_email) && (
                    <p className="mt-3 font-mono text-[11px] tracking-wider text-slate-500" data-testid="history-masked-contact">
                      On file: {[result.contact_phone, result.contact_email].filter(Boolean).join(" · ")}
                    </p>
                  )}

                  <OilTrac data={result.oil_trac} />

                  <div className="mt-2 divide-y divide-white/5">
                    {result.records.map((r, i) => (
                      <motion.div
                        key={r.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.08 }}
                        className="py-5"
                        data-testid={`history-record-${r.id}`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h4 className="text-base font-semibold text-slate-100">{r.title}</h4>
                          <span className="font-mono text-xs text-slate-500">{fmtDate(r.service_date)}</span>
                        </div>
                        {r.description && <p className="mt-2 text-sm leading-relaxed text-slate-400">{r.description}</p>}
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
                          {r.odometer_km != null && <span>{r.odometer_km.toLocaleString()} km</span>}
                          {r.cost != null && <span className="text-amber-500">NZ${r.cost.toLocaleString()}</span>}
                          {r.oil_change && (
                            <span className="flex items-center gap-1.5 text-amber-500" data-testid={`record-oil-${r.id}`}>
                              <Droplets size={12} /> Oil {r.oil_grade ? `${r.oil_grade} ` : ""}{r.next_oil_due_km != null ? `· next ${r.next_oil_due_km.toLocaleString()} km` : ""}
                            </span>
                          )}
                          {r.next_service_due && (
                            <span className="flex items-center gap-1.5 text-teal-400">
                              <CalendarClock size={12} /> Next due {fmtDate(r.next_service_due)}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
